import { Checkbox, Divider, Input, Radio, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { useCallback, useEffect, useMemo } from "react";
import { VisitType } from "../../../domain/notes.domain";
import { useAppSelector } from "../../../hooks/redux-hooks";
import useSpecialityReference from "../../../hooks/useSpecialityReference";
import styles from "./AITemplateModal.module.scss";
import { NOTE_TEMPLATE_CLIENT, NOTE_TEMPLATE_PATIENT } from "./constants";
import type { CustomNote } from "./types";

interface TemplateState {
  name: string;
  document: string;
}

const Create = ({
  setIsPrimaryButtonDisabled,
  setCurrentTemplate,
  isEditing,
  currentTemplate,
  handleGoBack,
}: {
  setIsPrimaryButtonDisabled?: (disabled: boolean) => void;
  setCurrentTemplate: (template: CustomNote | null) => void;
  isEditing: boolean;
  currentTemplate: CustomNote | null;
  handleGoBack: () => void;
}) => {
  const componentName = "ai-template-modal";
  const [template, setTemplate] = React.useState<TemplateState>({
    name: isEditing ? currentTemplate?.name ?? "" : "",
    document: isEditing ? currentTemplate?.document ?? "" : "",
  });

  const [showTutorial, setShowTutorial] = React.useState(false);
  const [is_addon, setIsAddon] = React.useState(currentTemplate?.settings?.is_addon || false);
  const [is_cpt_codes, setIsCptCodes] = React.useState(currentTemplate?.settings?.is_cpt_codes || false);
  const [is_group_accessible, setIsGroupAccessible] = React.useState(currentTemplate?.is_group_accessible || false);
  const [visit_type, setVisitType] = React.useState<number | undefined>(currentTemplate?.settings?.visit_type);

  const { userInfo } = useAppSelector((state) => state.auth);
  const { shouldReferAsPatient } = useSpecialityReference();

  const shouldHideCptCodes = useMemo(() => {
    return userInfo?.practice_area && userInfo?.practice_area !== 0;
  }, [userInfo]);

  // Initialize template from currentTemplate
  useEffect(() => {
    if (currentTemplate) {
      setTemplate({
        name: currentTemplate.name || "",
        document: currentTemplate.document || "",
      });
      setIsAddon(currentTemplate.settings?.is_addon || false);
      setIsCptCodes(currentTemplate.settings?.is_cpt_codes || false);
      setIsGroupAccessible(currentTemplate.is_group_accessible || false);
      setVisitType(currentTemplate.settings?.visit_type);
    }
  }, [currentTemplate]); // Only run when template ID changes

  // Handle template changes
  const handleTemplateChange = useCallback(
    (newTemplate: TemplateState) => {
      setTemplate(newTemplate);
      setCurrentTemplate({
        ...(currentTemplate || {}),
        name: newTemplate.name,
        document: newTemplate.document,
        settings: {
          is_addon,
          is_cpt_codes,
          visit_type,
        },
        is_group_accessible,
      } as CustomNote);
    },
    [currentTemplate, is_addon, is_cpt_codes, visit_type, is_group_accessible, setCurrentTemplate],
  );

  useEffect(() => {
    const tutorialState = localStorage.getItem("tutorialState");
    if (tutorialState) {
      setShowTutorial(tutorialState === "true");
    } else {
      setShowTutorial(true);
    }
  }, []);

  useEffect(() => {
    if (setIsPrimaryButtonDisabled) {
      setIsPrimaryButtonDisabled(
        template.name.length === 0 || template.document.length === 0 || visit_type === undefined,
      );
    }
  }, [template, setIsPrimaryButtonDisabled, visit_type]);

  return (
    <div style={{ height: "calc(100vh - 60px)", display: "flex", flexDirection: "column" }}>
      <div className={styles[`${componentName}__header`]}>
        <div className={styles[`${componentName}__header-container-create`]}>
          <button onClick={handleGoBack} className={styles[`${componentName}__header-close-button`]}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              width={20}
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
            </svg>
          </button>

          <h3>{isEditing ? "Edit" : "Create"} My Template</h3>
        </div>
      </div>

      <Divider style={{ marginBlock: "0" }} />

      {showTutorial && (
        <a href="https://getnudgeai.com/custom-templates" target="_blank" rel="noopener noreferrer">
          <div className={styles[`${componentName}__tutorial-container`]}>
            <div>
              <div>
                <img src="/assets/tutorial.png" alt="tutorial" width={57} height={40} />
                <div>
                  <h4>How to create a custom template using AI</h4>
                  <p>Learn through our interactive tutorial and start building in just 2 steps</p>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  localStorage.setItem("tutorialState", "false");
                  setShowTutorial(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  width={20}
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </a>
      )}

      <div
        className={styles[`${componentName}__create-container`]}
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <div>
          <Typography.Text>Template name</Typography.Text>
          <Input
            className={`${styles[`${componentName}__create-name`]}`}
            placeholder="Type template name"
            value={template.name}
            onChange={(e) => handleTemplateChange({ name: e.target.value, document: template.document })}
            maxLength={100}
          />
        </div>
        <div
          className={styles[`${componentName}__create-description`]}
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Typography.Text>
            Paste your ideal note or document, for example: your progress note, intake, treatment plan, assessments,
            discharge notes and more, and turn it into a template
          </Typography.Text>
          <a
            href="https://getnudgeai.com/custom-templates"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginTop: "4px" }}
          >
            Learn more from examples
          </a>
          <TextArea
            placeholder={shouldReferAsPatient ? NOTE_TEMPLATE_PATIENT : NOTE_TEMPLATE_CLIENT}
            style={{ flex: 1 }}
            value={template.document}
            onChange={(e) => handleTemplateChange({ name: template.name, document: e.target.value })}
          />
        </div>
        <div style={{ marginTop: "16px", marginBottom: "16px" }}>
          <Typography.Text style={{ display: "block", marginBottom: "8px" }}>
            Visit Type <span style={{ color: "#ff4d4f" }}>*</span>
          </Typography.Text>
          <Radio.Group
            value={visit_type}
            onChange={(e) => {
              const value = e.target.value;
              setVisitType(value);
              setCurrentTemplate({
                ...(currentTemplate || {}),
                name: template.name,
                document: template.document,
                settings: {
                  is_addon,
                  is_cpt_codes,
                  visit_type: value,
                },
                is_group_accessible,
              } as CustomNote);
            }}
          >
            <Radio value={VisitType.TYPE_INTAKE}>Intake</Radio>
            <Radio value={VisitType.TYPE_FOLLOWUP}>Followup</Radio>
            <Radio value={VisitType.TYPE_OTHER}>Other</Radio>
          </Radio.Group>
        </div>
        {!shouldHideCptCodes ? (
          <div
            className={styles[`${componentName}__create-checkbox`]}
            style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
          >
            {userInfo?.is_group_admin && (
              <div
                style={{
                  backgroundColor: "#E6F7FF", // Light blue background
                  borderRadius: "4px",
                  padding: "0px", // No padding
                }}
              >
                <Checkbox
                  onChange={(e) => {
                    setIsGroupAccessible(e.target.checked);
                    setCurrentTemplate({
                      ...(currentTemplate || {}),
                      name: template.name,
                      document: template.document,
                      settings: {
                        is_addon,
                        is_cpt_codes,
                        visit_type,
                      },
                      is_group_accessible: e.target.checked,
                    } as CustomNote);
                  }}
                  checked={is_group_accessible}
                  style={{ color: "#0050B3" }} // Darker blue text for contrast on light blue bg
                >
                  Share this template with my group members
                </Checkbox>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Create;
